// @givmo/app-charity — this class's sidebar DECLARATIONS, as data (shell contract v3, T6).
//
// Every authenticated page wears the ONE layout — the sidebar (a drawer below `lg`) and the top
// bar — and what differs per page is the sidebar's CONTENT, decided by the package's ONE rule
// (`sidebarFor`) over the declarations here:
//
//   * a MOUNTED MODULE's page → the module's own declared rows (`modules`: Donations declares
//     `Donations` · `Payments` (A5/A7); Compliance its three rows while its knob is on; Board
//     its six pages, from its package, while its knob is on) + the
//     shell's own Team row (`<module>/team`, requiring that module's approval string — the package
//     gives it, a module adds nothing) + the Organization section;
//   * a declared NON-MODULE page (`pages`) → its own rows: the Admin Console family on every
//     `/org/…` page, the statement page at `/apps`, and a title for every class page that has no
//     sidebar row of its own;
//   * an ORGANIZATION-SECTION page (Profile) → the frame of the module it was opened from (the
//     2026-09-04 walkthrough ruling): the shell hands `frameModule` — the last-module hint — and
//     `frameFallback` — the class default, Donations — and the package frames with the first of the
//     two that is mounted and that this person can open, with Profile the active row and the title;
//   * the SETTINGS page (the gear's, `moduleFrame`) → the sidebar of the page the gear was pressed
//     on, exactly as it renders there (D-CPO-33: "the sidebar framed by the gear's ORIGIN" — the
//     Admin Console's rows from an Admin Console page, Donations' rows from a Donations page); on a
//     direct load, the same frame rule as an Organization page, and the Organization section alone
//     when nothing frames it. It declares NO rows of its own: the per-section anchor rows the page
//     once carried (Landing · Notifications) were REJECTED at D-CPO-34, and where a Settings row
//     lives in the sidebar is the settings framework's to decide (provisional OM-17);
//   * anything else → the Organization section alone, the honest minimum (the not-found page, and
//     an Organization page whose frame does not apply).
//
// ══ THE ORGANIZATION SECTION IS CLASS DATA ═════════════════════════════════════════════════════
//
// The charity class's ONE page that is not a module: Profile — the live CPA-WEB6 editor while
// `VITE_PROFILE_PAGE_ENABLED` is unset (the same tree, gates, ids, states and pixels, plus one wrapper
// element — the Profile Enrichment invariant; this row is unchanged byte for byte), the tabbed
// Profile page once it is set (PE-WEB1), the row re-keyed to `profile:read` with that same reveal.
// Fundraisers and its campaign pages wear the shared module's own frame; the former class-only
// fundraiser/P2P rows are retired.
// Admin management is the Admin Console's Team access page (`/org/team-access`, the package's
// composer + roster), never an Organization row: the legacy `Admins` row and its `/admins` page
// retired with CPA-WEB41, and that address is an unknown path — no redirect.
//
// The rows' ids ARE the testid contract: the package renders `menu-<id>`, so `profile` →
// `menu-profile`, unchanged. Fundraisers uses `menu-fundraisers` in its module frame.
// The former class-only fundraiser/P2P rows and `menu-admins` are retired.
//
// ⚠ NO Organization section on the Admin Console pages (walkthrough decision, 2026-09-03), and NO
// Home row anywhere (R2).

import type { LucideLikeIcon } from '@givmo/design-system';
import { COMPLIANCE_READ_PERMISSION, COMPLIANCE_ROUTES, COMPLIANCE_SLUG } from '@givmo/compliance';
import { BOOKS_NAV } from '@givmo/books';
import { SOCIAL_NAV } from '@givmo/social';
import { BOARD_NAV, BOARD_SETTINGS } from '@givmo/board';
import { FUNDRAISERS_ROOT_ROW, FUNDRAISERS_SLUG, fundraisersNav } from '@givmo/fundraisers-module';
import type {
  ModuleNav,
  NavRowDeclaration,
  SectionRowDeclaration,
  OrganizationSettingsCard,
  PageNav,
  SidebarDeclarations,
} from '@givmo/org-console';
import {
  CalendarClock,
  ChartNoAxesColumn,
  FolderOpen,
  HandCoins,
  Landmark,
  ListFilter,
  ScrollText,
  Store,
  Users,
  UsersRound,
} from 'lucide-react';
import { orgPageNav, type OrgPageReveals } from '../features/org/org-pages';
import { COMPLIANCE_SETTINGS } from '../features/compliance/compliance-settings';
import { OrgConnectedAccountsCard } from '../features/preferences/org-connected-accounts-card';
import { OrgRemovalCard } from '../features/preferences/org-removal-card';
import { CHARITY_FUNDRAISERS_FLAVOR } from '../features/fundraisers-module/fundraisers-mount';
import {
  isFundraisersModuleEnabled,
  isCharityDonorAnalyticsEnabled,
  isBooksModuleEnabled,
  isBoardUiEnabled,
  isComplianceUiEnabled,
  isProfilePageEnabled,
} from '../lib/env';
import { SETTINGS_ROUTES } from '../features/settings/settings-routes';
import { ANALYTICS_ROUTES } from '../features/analytics/analytics-routes';
import { APPS_ROUTE, DONATIONS_ROUTE, ORGANIZATIONS_ROUTE, SETTINGS_ROUTE } from './console-routes';
import { CLASS_DEFAULT_SLUG, DONATIONS_READ_PERMISSION } from './module-table';

/** The org-level read string the Profile PAGE requires once it is revealed (PE-BE5, plan §2.4). */
export const PROFILE_READ_PERMISSION = 'profile:read';

/**
 * The Profile row — always present. Its `requires` re-keys WITH the reveal (PE-WEB1): while the
 * knob is unset the live CPA-WEB6 editor answers `/profile` under its own Owner gate and the row
 * stays the plain link it has always been (the live-route invariant); once the tabbed page is
 * revealed the row requires the page's read string, so a Member without `profile:read` meets the
 * greyed row, never a page whose every read the server refuses. Read at call time, like every
 * knob here.
 */
function profileRow(): NavRowDeclaration<LucideLikeIcon> {
  return {
    id: 'profile',
    label: 'Profile',
    icon: Store,
    destination: { kind: 'org', path: '/profile' },
    requires: isProfilePageEnabled() ? PROFILE_READ_PERMISSION : '',
  };
}

/** The Organization section is class data; module rows live in their own frame. */
export function organizationRows(): NavRowDeclaration<LucideLikeIcon>[] {
  return [profileRow()];
}

/**
 * The Donations module's nav (A5/A7): `Donations` (the statement — the Statement tab's word
 * dissolved into this row) and `Payments`, both behind the module's read string. The shell adds
 * the Team row; the Organization section follows.
 *
 * The Donations-owned analytics page (CRM-1 split) keeps its row here while its own knob is on —
 * compiled dark everywhere today. Its address is UNDER the module (`/donations/analytics`), so the
 * page wears this frame whether its row is followed or its address is loaded; a module row into a
 * page outside the module would drop the frame there (the page branch knows no owner).
 *
 * `Fundraisers` is BORROWED (D-CPO-40 (3); Org Machinery OM-18; FCM-WEB2): a reference to the
 * Fundraisers module's own root row — its address and its string stay that row's, so a person needs
 * the module's read string to open it and a Donations admin without it meets the request row. Followed
 * from this sidebar, `/fundraisers` and every page beneath it wear the Donations frame with this row
 * lit; a direct load wears Fundraisers' own. While this build does not mount Fundraisers the package
 * omits the row (the host rail pins that it resolves wherever Fundraisers mounts).
 */
export function donationsNav(): ModuleNav<LucideLikeIcon> {
  const rows: SectionRowDeclaration<LucideLikeIcon>[] = [
    {
      id: 'donations',
      label: 'Donations',
      icon: HandCoins,
      destination: { kind: 'module', slug: CLASS_DEFAULT_SLUG },
      requires: DONATIONS_READ_PERMISSION,
    },
    {
      id: 'payments',
      label: 'Payments',
      icon: Landmark,
      destination: { kind: 'module', slug: CLASS_DEFAULT_SLUG, subpath: '/payments' },
      requires: DONATIONS_READ_PERMISSION,
    },
  ];
  if (isCharityDonorAnalyticsEnabled()) {
    rows.push({
      id: 'analytics',
      label: 'Analytics',
      icon: ChartNoAxesColumn,
      destination: {
        kind: 'module',
        slug: CLASS_DEFAULT_SLUG,
        subpath: ANALYTICS_ROUTES.dashboard.slice(DONATIONS_ROUTE.length),
      },
      requires: '',
    });
  }
  rows.push({ id: 'fundraisers', borrow: { module: FUNDRAISERS_SLUG, row: FUNDRAISERS_ROOT_ROW } });
  return {
    slug: CLASS_DEFAULT_SLUG,
    sections: [{ id: 'donations', label: 'Donations', rows }],
    includeOrganizationSection: true,
  };
}

/** A compliance row's subpath under the module's root, derived so the two cannot drift. */
function complianceSubpath(path: string): string {
  return path.slice(COMPLIANCE_ROUTES.calendar.length);
}

/**
 * The Compliance module's nav (CCM-WEB1/2; CCM-MOD1B; re-composed at CCM-R1-INTAKE (a)) — Calendar ·
 * Filings · Documents (set C Q1, D-CCM-21), on the module's own pages only. The Calendar is the
 * module's root and its landing. The review queue and the recorded facts have no row: their
 * addresses redirect onto the Calendar (A-C10; D-CCM-14). The ids are the e2e identity mirror's
 * (`compliance-e2e-contract.json`); the section id keeps `menu-section-filings`.
 *
 * Every row requires the module's ONE read string, `compliance:read` (the Member catalog's, minted
 * with the D-CPO-24 flip — the backend half of CCM-MOD1B): a Member without the grant meets a
 * REQUEST row, never a live link into a gated door. The writes each page carries are gated by the
 * server; the row names what opening the page needs.
 */
export const COMPLIANCE_NAV: ModuleNav<LucideLikeIcon> = {
  slug: COMPLIANCE_SLUG,
  sections: [
    {
      id: 'filings',
      label: 'Filings',
      rows: [
        {
          id: 'compliance-calendar',
          label: 'Calendar',
          icon: CalendarClock,
          destination: { kind: 'module', slug: COMPLIANCE_SLUG },
          requires: COMPLIANCE_READ_PERMISSION,
        },
        {
          id: 'compliance-filings',
          label: 'Filings',
          icon: ScrollText,
          destination: {
            kind: 'module',
            slug: COMPLIANCE_SLUG,
            subpath: complianceSubpath(COMPLIANCE_ROUTES.filings),
          },
          requires: COMPLIANCE_READ_PERMISSION,
        },
        {
          id: 'compliance-documents',
          label: 'Documents',
          icon: FolderOpen,
          destination: {
            kind: 'module',
            slug: COMPLIANCE_SLUG,
            subpath: complianceSubpath(COMPLIANCE_ROUTES.documents),
          },
          requires: COMPLIANCE_READ_PERMISSION,
        },
      ],
    },
  ],
  includeOrganizationSection: true,
};

/** The CRM module's nav while its knob mounts it (`donors` → `/crm`): Contacts, and CRM-5's Segments. */
export const DONORS_NAV: ModuleNav<LucideLikeIcon> = {
  slug: 'donors',
  sections: [
    {
      id: 'donors',
      label: 'CRM',
      rows: [
        {
          id: 'contacts',
          label: 'Contacts',
          icon: Users,
          destination: { kind: 'module', slug: 'donors' },
          requires: '',
        },
        {
          id: 'segments',
          label: 'Segments',
          icon: ListFilter,
          destination: { kind: 'module', slug: 'donors', subpath: '/segments' },
          requires: '',
        },
      ],
    },
  ],
  includeOrganizationSection: true,
};

/**
 * The settings page — the gear's ONE composed page (D-CPO-33): the current module's settings, the
 * person's own preferences, and every org-level setting they hold permission for, at the foot.
 *
 * It declares no rows and asks for the frame of the page it was opened from (`moduleFrame`; the
 * shell hands the origin as `framePath`). With nothing to frame it — a direct load with no hint and
 * no openable class default — it wears the Organization section alone, the honest minimum every
 * undeclared page wears. The title is the page-only breadcrumb's word.
 */
export const SETTINGS_NAV: PageNav<LucideLikeIcon> = {
  path: SETTINGS_ROUTE,
  title: 'Settings',
  includeOrganizationSection: true,
  moduleFrame: true,
  sections: [],
};

/**
 * The statement page at `/apps` (a holder of `write_settings` never sees it — the route replaces
 * them onto the Admin Console): the Organization section by the default rule, titled "Apps".
 */
export const APPS_NAV: PageNav<LucideLikeIcon> = {
  path: APPS_ROUTE,
  title: 'Apps',
  includeOrganizationSection: true,
  sections: [],
};

/**
 * Every routable class page with NO sidebar row of its own, so the page-only breadcrumb names it:
 * the claim flow (its nav group retired with D-CP-36, the routes live), the invite landing, the
 * once-ever setup flow (CBO-IN3, no row in any build), the terms page, the catalog-removal request
 * page ("Remove from Givmo" at `/removal` — the composed settings page's card is its door), the
 * support page, the organization deep link (a doorway that paints nothing, titled for the frame the
 * shell holds while it decides — CPA-BE29). Each wears the Organization section, the honest minimum.
 */
export const CLASS_PAGE_NAVS: readonly PageNav<LucideLikeIcon>[] = [
  { path: '/claim', prefix: true, title: 'Claim an organization', sections: [] },
  { path: '/claims', prefix: true, title: 'My claims', sections: [] },
  { path: '/invites/accept', title: 'Accept invite', sections: [] },
  { path: ORGANIZATIONS_ROUTE, prefix: true, title: 'Your organization', sections: [] },
  { path: '/enrollment', prefix: true, title: 'Organization setup', sections: [] },
  { path: '/terms', title: 'Charity Terms', sections: [] },
  { path: SETTINGS_ROUTES.removal, title: 'Remove from Givmo', sections: [] },
  { path: '/support', title: 'Support', sections: [] },
];

/** The shell's Team row glyph — two people. */
export const TEAM_ROW_ICON: LucideLikeIcon = UsersRound;

/**
 * This class's Organization-section cards on the composed settings page (OM-17 design 3): the
 * catalog-removal card — a GRAVE act, a DOOR into the shipped removal-request page — for a holder
 * of `charity:remove_request` (the string off the standing read; the page keeps its own gate).
 * The composer orders grave acts after every knob and *Deactivate organization* last.
 */
export const REMOVAL_CARD: OrganizationSettingsCard = {
  id: 'removal',
  order: 10,
  requires: 'charity:remove_request',
  grave: true,
  component: OrgRemovalCard,
};

/**
 * The Organization section's "Connected accounts" card (CBG-CAL1 slice 1d; question 1): every account
 * outside Givmo the organization lets Givmo use, for the settings authority's CAPABILITY
 * (`write_settings`, which no module switch masks — revoking access never waits on Board being on).
 * A knob, not a grave act. Not behind the Board knob: it renders nothing until core serves the list
 * and lists something, and with Board off it offers only core's disconnect.
 */
export const CONNECTED_ACCOUNTS_CARD: OrganizationSettingsCard = {
  id: 'connected-accounts',
  order: 5,
  capability: 'write_settings',
  component: OrgConnectedAccountsCard,
};

export interface ConsoleDeclarationOptions {
  /** The reserved page's probe map (nothing reveals today). */
  reveals?: OrgPageReveals;
}

/**
 * The declarations for this visit. `capabilities` narrows the Admin Console family to the pages this
 * person may open.
 *
 * `moduleSettings` and `organizationSettings` are the composed settings page's two declared seams
 * (OM-17 design 4 + design 3): the panels each mounted module DECLARES for its "<Module> settings"
 * section, beside its `ModuleNav`, and this class's Organization-section cards (the removal card).
 * Board is the first declarer (CBG-MOD1; D-CBG-9): its calendar's two cards (CBG-CAL1), record
 * retention and legal holds, compiled in with the Board knob exactly as its nav is. Compliance declares "Use AI on your
 * organization’s data" (CCM-R1-AI-SETTING), compiled in with the Compliance knob as its routes are.
 */
export function consoleDeclarations(
  capabilities: readonly string[] | null,
  options: ConsoleDeclarationOptions = {},
): SidebarDeclarations<LucideLikeIcon> {
  return {
    modules: [
      donationsNav(),
      COMPLIANCE_NAV,
      DONORS_NAV,
      ...(isBooksModuleEnabled() ? [BOOKS_NAV] : []),
      ...(isFundraisersModuleEnabled() ? [fundraisersNav(CHARITY_FUNDRAISERS_FLAVOR)] : []),
      // Social (SMM-WEB1): mounted in every build, so declared in every build.
      SOCIAL_NAV,
      ...(isBoardUiEnabled() ? [BOARD_NAV] : []),
    ],
    organization: organizationRows(),
    pages: [
      orgPageNav(capabilities, options.reveals ?? {}),
      SETTINGS_NAV,
      APPS_NAV,
      ...CLASS_PAGE_NAVS,
    ],
    teamIcon: TEAM_ROW_ICON,
    moduleSettings: [
      ...(isComplianceUiEnabled() ? [COMPLIANCE_SETTINGS] : []),
      ...(isBoardUiEnabled() ? [BOARD_SETTINGS] : []),
    ],
    organizationSettings: [CONNECTED_ACCOUNTS_CARD, REMOVAL_CARD],
  };
}
